import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Plot from 'react-plotly.js';
import { ManRadioButtonGroup, ManRadioButton, ManText } from '@craft-design-system/core-components-react';
import useGraphRadio from '../../utils/useGraphRadio';
import Shimmer_graph from '../shimmer_graph';
import PopoverComponent from '../popover';
import { Card } from 'react-bootstrap';
import AverageDisplay from './Average_value';
import SegmentedControl from './SegmentedControl';
import useSegmentedChartQuery from '../../utils/useSegmentedChartQuery';
import Zoom from '../Zoom';
import DownloadButtonComponent from '../DownloadButtonComponent';
import { useFilterContext } from '../FilterContext';
import AIToggleButton from '../AIToggleButton';

const AmbientTemperature = ({ value }) => {
  const [ambient_temperature, set_ambient_temperature] = useState(null);
  const radio_query = useGraphRadio();
  const [radio_buttons_test, set_Radio_values] = useState(null);
  const { filterdata } = useFilterContext();
  const { startDate, endDate } = filterdata || {};
  const [isLoading, setIsLoading] = useState(false);
  const [showMonthlyText, setShowMonthlyText] = useState(value.Ambienttemperature[1]['ambient_normal_charging']);
  const [avg_value, setavg_value] = useState(value.Ambienttemperature[2]['average']);
  const unit = '°C';
  const [downloadData, setDownloadData] = useState(JSON.parse(value.Ambienttemperature[3]));
  const [chartType, setChartType] = useState("Bar");
  const { chartData, fetchSegmentedChart } = useSegmentedChartQuery();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const handleDownloadClick = () => {
    if (!Array.isArray(downloadData) || downloadData.length === 0 || !downloadData[0] || Object.keys(downloadData[0]).length === 0) {
      setShowModal(true);
      return;
    }
    const csvContent = convertToCSV(downloadData);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'energy_consumption_bbp_data.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const convertToCSV = (data) => {
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row =>
      Object.values(row)
        .map(value => {
          const stringValue = String(value);
          if (/[",\n]/.test(stringValue)) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }
          return stringValue;
        })
        .join(',')
    );
    return [headers, ...rows].join('\n');
  };

  // Handle segmented chart fetch
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await fetchSegmentedChart({
          vinShort: value.vin_short,
          chartType,
          columnname: "Ambient Temperature",
        });

        console.log("📊 Segmented chart response:", data);
        
        if (chartType === "Bar") {
          set_ambient_temperature(JSON.parse(data.graph_data[0]));
          setDownloadData(JSON.parse(data.graph_data[3]));
        } else {
          set_ambient_temperature(JSON.parse(data[0]));
          setDownloadData(JSON.parse(data[1]));
        }
      } catch (error) {
        console.error("❌ Error fetching chart data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!isInitialLoad && chartType && value?.vin_short) {
      fetchData();
    } else {
      setIsInitialLoad(false);
    }
  }, [chartType]);

  const handleSelectChange1 = (event) => {
    set_Radio_values((prevValues) => ({
      ...prevValues,
      [event.detail.name]: event.detail.value,
    }));
  };

  // Handle radio button fetch
  useEffect(() => {
    if (radio_buttons_test) {
      console.log("🔄 Radio button changed:", radio_buttons_test);

      setIsLoading(true);

      const requestParams = {
        vins_short: [value.vin_short],
        radio_value: radio_buttons_test,
        columnname: "Ambient Temperature",
        startDate,
        endDate,
        filterdata: filterdata,
        resetdata: filterdata?.resetdata
      };

      console.log("📡 Fetching graph data with:", requestParams);

      radio_query(requestParams)
        .then(data => {
          console.log("✅ Response from radio_query:", data);

          try {
            const graphData = JSON.parse(data.jsonData.graph_data[0]);
            const avgVal = data.jsonData.graph_data[2]?.average;
            const monthlyText = data.jsonData.graph_data[1]?.ambient_normal_charging;

            set_ambient_temperature(graphData);
            setavg_value(avgVal);
            setShowMonthlyText(monthlyText);
            setDownloadData(JSON.parse(data.jsonData.graph_data[3]));
            
            console.log("📊 Graph updated:", graphData);
            console.log("📈 Average value:", avgVal);
          } catch (err) {
            console.error("❌ Error parsing graph_data:", err);
          }
        })
        .catch(error => {
          console.error("❌ Error in radio_query:", error);
        })
        .finally(() => {
          console.log("⏳ Finished radio_query fetch cycle");
          setIsLoading(false);
        });
    }
  }, [radio_buttons_test]);

  // Initial fallback data
  useEffect(() => {
    try {
      set_ambient_temperature(JSON.parse(value.Ambienttemperature[0]));
      setDownloadData(JSON.parse(value.Ambienttemperature[3]));
    } catch (error) {
      console.error("Error parsing fallback ambient_temperature JSON", error);
    }
  }, [value.Ambienttemperature]);



  return (
    <Card className="card_ride">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px',
        }}
      >
        <div style={{ flex: 1, textAlign: 'center' }}>
          <ManText className="card-title-text" variant="component-l" as="span" size="l">
            Ambient temperature
          </ManText>
        </div>
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          flexDirection: "row-reverse"
        }}>
          <PopoverComponent
            id="Ambient_Temperature"
            content="<b>Ambient Temperature:</b> This KPI displays the ambient temperature in which the vehicle is operating.<br/><br/><b>Telematic Raw Data:</b><br/>• TBM3: payloaddecoded_ambienttemperature_value<br/>• CM4: payload_electricmessagesystem_ambienttemperature<br/><br/><b>Time Series Data:</b><br/>If multiple vehicles are selected average values are displayed. Days without data are not taken into account for the calculation.<br/><br/><b>Histogram:</b><br/>Each bar represents the number of days that fall within a certain range (bin) of <b>daily average ambient temperature values</b>. All daily values since start of data acquisition for the selected vehicle(s) are visualized per default.<br/><br/><b>Boxplot:</b><br/>The boxplot shows the <b>distribution of daily average ambient temperature values</b> for the selected vehicle(s) since start of data acquisition. If multiple vehicles are selected, the boxplot reflects the <b>combined distribution</b> of all their daily values.<br/>• The <b>box</b> represents the <b>middle 50%</b> of values (from the 25th percentile ('q1') to the 75th percentile ('q3'))<br/>• The <b>line inside the box</b> shows the <b>median</b> (50th percentile)<br/>• The whiskers extend to show the range of typical values ('min' and 'max') and visualize <b>the spread of the data</b> beyond the central box."
          />
          <Zoom
            chartData={ambient_temperature?.data}
            chartLayout={ambient_temperature?.layout}
            chartTitle="Ambient Temperature"
          />
          <DownloadButtonComponent data={downloadData} fileName='ambient_temperature_data.xlsx' sheetName="Ambient temp Data" />
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '20px',
          marginTop: '16px',
        }}
      >
        {chartType === 'Bar' ? (
          <div style={{ flexShrink: 0 }}>
            <AverageDisplay averages={avg_value} unit="°C" />
          </div>
        ) : (
          <div style={{ width: '150px' }} />
        )}

        {chartType === 'Bar' ? (
          <ManRadioButtonGroup
            name="Ambient Temperature"
            invalid="false"
            onManChange={handleSelectChange1}
            style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}
          >
            <ManRadioButton
              name="Ambient Temperature"
              label="Daily"
              value="Daily"
              checked
            />
            <ManRadioButton
              name="Ambient Temperature"
              label="Monthly"
              value="Monthly"
            />
            <ManRadioButton
              name="Ambient Temperature"
              label="Yearly"
              value="Yearly"
            />
          </ManRadioButtonGroup>
        ) : (
          <div style={{ flexGrow: 1 }} />
        )}

        <div style={{ flexShrink: 0 }}>
          <SegmentedControl selected={chartType} onChange={(type) => setChartType(type)} />
        </div>
      </div>

      {isLoading ? (
        <Shimmer_graph />
      ) : (
        ambient_temperature && (
          <>
            <Plot
              data={ambient_temperature.data}
              layout={{
                ...ambient_temperature.layout,
                autosize: true,
                responsive: true,
                margin: chartType === 'Bar' 
                  ? { t: 10, r: 10, l: 10, b: 10 }
                  : { t: 10, r: 10, l: 50, b: 200 }
              }}
              useResizeHandler={true}
            />

            {/* 🔥 AI Toggle Button - Only show for Bar charts with data */}
            {chartType === 'Bar' && downloadData?.length > 0 && (
              <div style={{
                display: 'flex',
                justifyContent: 'flex-start',
                paddingLeft: '10px',
                marginTop: '8px',
                marginBottom: '8px'
              }}>
                <AIToggleButton
                  downloadData={downloadData}
                  radioButtonState={radio_buttons_test || { 'Ambient Temperature': 'Daily' }}
                  kpiName="Ambient Temperature"
                  chartType={chartType}
                  vinShort={value.vin_short}
                  title="AI | Automotive Intelligence - Ambient Temperature Analysis"
                />
              </div>
            )}
          </>
        )
      )}
    </Card>
  );
};

AmbientTemperature.propTypes = {
  value: PropTypes.shape({
    vin_short: PropTypes.string.isRequired,
    Ambienttemperature: PropTypes.array.isRequired,
  }).isRequired,
};

export default AmbientTemperature;